import React, { useState } from "react";
import { BiPencil } from "react-icons/bi";
import { HiOutlineTrash } from "react-icons/hi";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import { getNameProfile } from "Utils/utils";
import { canDeleteCosCaseNote } from "helpers/RolesPermission";
import cosApi from "store/services/cos.services";
import ahApi from "store/services/ah.services";
import CosCaseNoteAttachments from "./CosCaseNoteAttachments";
import {
  formatCosCaseNoteCreatedDate,
  formatCosCaseNoteDeliveredDate,
  formatCosCaseNoteDuration,
  getCosCaseNoteListPreviewText,
  getCosCaseNoteStatusLabel,
  hasCosCaseNoteListBody,
  isCosCaseNoteBillable,
  isCosCaseNoteLocked,
  sanitizeNoteHtml,
  shouldShowCosCaseNoteMore,
} from "./cosCaseNoteDisplayUtils";

const Chip = ({ children, className = "" }) => (
  <span
    className={`inline-flex flex-shrink-0 items-center px-1.5 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

const CosCaseNoteCard = ({
  note,
  onEdit,
  onDelete,
  isUserGracePeriodOver,
  canDelete = canDeleteCosCaseNote,
  ptUuid,
  caseNoteApiModule = "cos",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [fullNotes, setFullNotes] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const locked = isCosCaseNoteLocked(note);
  const hasNoteBody = hasCosCaseNoteListBody(note);
  const previewText = getCosCaseNoteListPreviewText(note);
  const showMoreButton = shouldShowCosCaseNoteMore(note);
  const noteHtml = fullNotes ? sanitizeNoteHtml(fullNotes) : "";
  const authorName = note?.created_by || "Unknown";
  const relativeCreated = note?.created_at
    ? formatDistanceToNow(new Date(note.created_at), { addSuffix: true })
    : "";

  const fetchFullNotes = async () => {
    if (!ptUuid || !note?.uuid) {
      throw new Error("Missing participant or case note identifier");
    }

    const api = caseNoteApiModule === "ah" ? ahApi : cosApi;
    const response = await api.getCaseNotesDetails({
      uuid: note.uuid,
      pt_id: ptUuid,
    });

    return response?.data?.notes ?? null;
  };

  const handleToggleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    if (!showMoreButton) {
      setExpanded(true);
      return;
    }

    if (fullNotes) {
      setExpanded(true);
      return;
    }

    setLoadingNotes(true);
    try {
      const notes = await fetchFullNotes();
      setFullNotes(notes);
      setExpanded(true);
    } catch (error) {
      console.error("Failed to load case note content:", error);
      toast.error("Failed to load full case note. Please try again.");
    } finally {
      setLoadingNotes(false);
    }
  };

  return (
    <div className='bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-150'>
      <div className='px-3 py-1.5 bg-slate-50 border-b border-gray-200 flex items-center justify-between gap-2'>
        <div className='min-w-0'>
          <div className='text-xs font-semibold text-gray-900'>
            Delivered: {formatCosCaseNoteDeliveredDate(note)}
          </div>
          <div className='text-[11px] text-gray-500 truncate'>
            Created: {formatCosCaseNoteCreatedDate(note)}
            {note?.delivered_service_number
              ? ` · ${note.delivered_service_number}`
              : ""}
          </div>
        </div>
        <div className='flex items-center gap-0.5 flex-shrink-0'>
          <button
            type='button'
            className={`p-1 rounded transition ${
              locked || isUserGracePeriodOver
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-blue-50 cursor-pointer"
            }`}
            disabled={locked || isUserGracePeriodOver}
            title={
              locked ? "Cannot edit — claim or invoice generated" : "Edit"
            }
            onClick={() => {
              if (locked || isUserGracePeriodOver) return;
              onEdit(note);
            }}
          >
            <BiPencil className='text-blue-600 w-3.5 h-3.5' />
          </button>
          {canDelete() && (
            <button
              type='button'
              className={`p-1 rounded transition ${
                isUserGracePeriodOver
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-red-50 cursor-pointer"
              }`}
              disabled={isUserGracePeriodOver}
              title='Delete'
              onClick={() => {
                if (isUserGracePeriodOver) return;
                onDelete(note);
              }}
            >
              <HiOutlineTrash className='text-red-500 w-3.5 h-3.5' />
            </button>
          )}
        </div>
      </div>

      <div className='flex gap-2.5 p-2.5 sm:p-3'>
        <div className='flex-shrink-0'>
          <img
            alt='avatar'
            className='h-8 w-8 rounded-full object-cover ring-1 ring-gray-200'
            src={getNameProfile(authorName, " ")}
          />
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-1.5 flex-wrap mb-1.5'>
            <span className='font-semibold text-sm text-gray-900 capitalize'>
              {authorName}
            </span>
            {relativeCreated && (
              <span
                className='text-[11px] text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5'
                title={formatCosCaseNoteCreatedDate(note)}
              >
                {relativeCreated}
              </span>
            )}
            {locked && (
              <Chip className='bg-amber-50 text-amber-800 border-amber-200'>
                Locked
              </Chip>
            )}
          </div>

          {note?.subject && (
            <div className='mb-1.5 min-w-0'>
              <span
                className='inline-flex max-w-full items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 overflow-hidden text-ellipsis whitespace-nowrap'
                title={note.subject}
              >
                {note.subject}
              </span>
            </div>
          )}

          <div className='flex flex-wrap items-center gap-1 mb-1.5 min-w-0'>
            <Chip
              className={
                isCosCaseNoteBillable(note)
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-red-50 text-red-700 border-red-200"
              }
            >
              {isCosCaseNoteBillable(note) ? "Billable" : "Non-billable"}
            </Chip>
            {note?.cos_contact_type?.title && (
              <Chip className='bg-indigo-50 text-indigo-700 border-indigo-200 capitalize'>
                {note.cos_contact_type.title}
              </Chip>
            )}
            <Chip className='bg-gray-50 text-gray-700 border-gray-200'>
              {getCosCaseNoteStatusLabel(note)}
            </Chip>
            <Chip className='bg-gray-50 text-gray-700 border-gray-200'>
              {formatCosCaseNoteDuration(note)}
            </Chip>
            {(note?.km ?? 0) > 0 && (
              <Chip className='bg-purple-50 text-purple-700 border-purple-200'>
                {note.km} km
              </Chip>
            )}
            {note?.external_sp?.name && (
              <Chip className='bg-slate-50 text-slate-700 border-slate-200 capitalize max-w-[140px] truncate'>
                {note.external_sp.name}
              </Chip>
            )}
            {note?.total_amount != null && (
              <Chip className='bg-emerald-50 text-emerald-700 border-emerald-200'>
                ${Number(note.total_amount).toFixed(2)}
              </Chip>
            )}
          </div>

          {hasNoteBody && (
            <div className='text-xs text-gray-700 bg-gray-50 rounded-md p-2 border border-gray-100'>
              {expanded && fullNotes ? (
                <div
                  style={{ wordBreak: "break-word" }}
                  className='prose prose-sm max-w-none text-xs'
                  dangerouslySetInnerHTML={{ __html: noteHtml }}
                />
              ) : (
                <p className='text-xs text-gray-700 whitespace-pre-wrap leading-relaxed'>
                  {previewText}
                </p>
              )}
              {showMoreButton && (
                <button
                  type='button'
                  className='mt-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50'
                  onClick={handleToggleExpand}
                  disabled={loadingNotes}
                >
                  {loadingNotes
                    ? "Loading…"
                    : expanded
                      ? "Show less"
                      : "Show more"}
                </button>
              )}
            </div>
          )}

          <CosCaseNoteAttachments note={note} />
        </div>
      </div>
    </div>
  );
};

export default CosCaseNoteCard;
